<template>
    <div>
        <div class="flex items-start gap-2 border-b border-gray-100 px-4 py-3">
            <div class="min-w-0 flex-1">
                <p class="text-sm font-medium break-words text-gray-900">{{ ask.current.question }}</p>

                <!-- How the question was read. Shown quietly, because a person
                     needs to be able to tell a misread question from a
                     surprising answer. -->
                <p v-if="ask.current.plan?.reasoning_note" class="mt-1 text-xs text-gray-500">
                    {{ ask.current.plan.reasoning_note }}
                </p>
            </div>

            <!-- Put it away without deleting it: it stays in history, this only
                 clears the pane. -->
            <button
                type="button"
                title="Close this answer"
                class="-mt-0.5 shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                @click="ask.dismiss()"
            >
                <XMarkIcon class="size-4" aria-hidden="true" />
                <span class="sr-only">Close this answer</span>
            </button>
        </div>

        <div v-if="ask.pending" class="flex items-center gap-3 px-4 py-8 text-sm text-gray-500">
            <Loader class="h-4 w-4" />
            Working through your data…
        </div>

        <div v-else-if="ask.current.status === 'failed'" class="px-4 py-6 text-sm text-red-700">
            {{ ask.current.error || 'That question could not be answered.' }}
        </div>

        <!-- Clarify is a real outcome, not an error state. -->
        <div v-else-if="ask.current.clarification" class="px-4 py-5">
            <p class="text-sm text-gray-800">{{ ask.current.clarification }}</p>
            <p class="mt-2 text-xs text-gray-500">Reply with a bit more detail and I'll try again.</p>
        </div>

        <div v-else class="px-4 py-4">
            <!-- The chips. The single most important trust affordance here: the
                 person must be able to see WHAT was asked, not only what came
                 back. -->
            <div v-if="ask.chips.length" class="mb-3 flex flex-wrap items-center gap-1.5">
                <span v-for="(chip, i) in ask.chips" :key="i" class="rounded-md bg-gray-100 px-2 py-1 font-mono text-xs text-gray-700">
                    {{ chip }}
                </span>
            </div>

            <p v-if="ask.current.narration" class="mb-3 text-sm leading-relaxed text-gray-800">
                {{ ask.current.narration }}
            </p>

            <!-- Measure -->
            <div v-if="ask.current.surface === 'measure'" class="space-y-3">
                <div>
                    <p :class="compact ? 'text-2xl' : 'text-3xl'" class="font-semibold text-gray-900">
                        {{ ask.formatValue(ask.result.value, ask.result.unit) }}
                    </p>
                    <p class="mt-0.5 text-xs text-gray-500">
                        {{ ask.result.label }} · {{ ask.result.window.from }} to {{ ask.result.window.to }}
                        <span v-if="ask.result.provisional" class="ml-1 text-amber-700">· provisional, the daily rollup has not caught up</span>
                    </p>
                </div>

                <p v-if="ask.result.compare" class="text-sm text-gray-700">
                    Previous period: {{ ask.formatValue(ask.result.compare.value, ask.result.unit) }}
                    <span :class="ask.result.compare.change >= 0 ? 'text-green-700' : 'text-red-700'">
                        ({{ ask.result.compare.change >= 0 ? '+' : '' }}{{ ask.formatValue(ask.result.compare.change, ask.result.unit)
                        }}<template v-if="ask.result.compare.change_percent !== null">, {{ ask.result.compare.change_percent }}%</template>)
                    </span>
                </p>

                <div v-if="ask.result.rows?.length" class="overflow-x-auto">
                    <table class="min-w-full text-sm">
                        <tbody>
                            <tr v-for="(row, i) in ask.result.rows" :key="i" class="border-t border-gray-100">
                                <td class="py-1.5 pr-6 text-gray-700">{{ ask.rowLabel(row) }}</td>
                                <td class="py-1.5 text-right font-medium text-gray-900 tabular-nums">
                                    {{ ask.formatValue(row.aggregate, ask.result.unit) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <p v-if="ask.result.note" class="text-xs text-gray-500">{{ ask.result.note }}</p>
            </div>

            <!-- List -->
            <div v-else-if="ask.current.surface === 'list'">
                <div v-if="!ask.result.rows?.length" class="text-sm text-gray-600">Nothing matched that.</div>

                <div v-else class="overflow-x-auto">
                    <table class="min-w-full text-sm">
                        <thead>
                            <tr class="border-b border-gray-200">
                                <th v-for="col in visibleColumns" :key="col" class="py-2 pr-6 text-left font-medium whitespace-nowrap text-gray-600">
                                    {{ ask.humanise(col) }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, i) in visibleRows" :key="i" class="border-b border-gray-100">
                                <td v-for="col in visibleColumns" :key="col" class="py-1.5 pr-6 whitespace-nowrap text-gray-800">
                                    {{ ask.cell(row, col) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="mt-3 flex flex-wrap items-center gap-4">
                    <p class="text-xs text-gray-500">
                        Showing {{ visibleRows.length }}<template v-if="ask.result.total"> of {{ ask.result.total }}</template>
                    </p>
                    <!-- The answer is a starting point, not a dead end. -->
                    <router-link
                        v-if="ask.result.route"
                        :to="ask.result.route"
                        class="text-xs font-medium text-indigo-600 hover:text-indigo-700"
                        @click="emit('navigated')"
                    >
                        Open as a table →
                    </router-link>
                </div>
            </div>

            <!-- Report -->
            <div v-else-if="ask.current.surface === 'report'">
                <p class="text-sm text-gray-800">{{ ask.result.label }} · {{ ask.result.window.from }} to {{ ask.result.window.to }}</p>
                <router-link
                    v-if="ask.result.route"
                    :to="ask.result.route"
                    class="mt-2 inline-block text-xs font-medium text-indigo-600 hover:text-indigo-700"
                    @click="emit('navigated')"
                >
                    Open the full report →
                </router-link>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import Loader from '@/components/Loader.vue';

const props = defineProps({
    /**
     * The whole useAsk() instance. Passed as one object rather than a dozen
     * props because this component is a view onto exactly one conversation and
     * threading each field through separately would only invite the two callers
     * to pass slightly different sets.
     */
    ask: { type: Object, required: true },
    /** The floating launcher is narrow; the page is not. */
    compact: { type: Boolean, default: false },
});

const emit = defineEmits(['navigated']);

// A messenger panel four hundred pixels wide cannot show eight columns or
// twenty-five rows legibly. Truncating here rather than asking for less keeps
// the two surfaces answering the identical question, so "open as a table"
// always lands on the full result.
const visibleColumns = computed(() => {
    const columns = props.ask.result.columns ?? [];
    return props.compact ? columns.slice(0, 3) : columns;
});

const visibleRows = computed(() => {
    const rows = props.ask.result.rows ?? [];
    return props.compact ? rows.slice(0, 8) : rows;
});
</script>
